import {
  type AIAgent,
  type AIConnector,
  getAgentId,
  getAgentOwnerEmail,
  getConnectorAgentId,
  getConnectorId,
} from "@p0security/shared/types/ai-inventory";
import type { ItemAssessmentScope } from "@p0security/shared/types/assessment";
import { Tabs } from "antd";
import { useEnumSearchParam } from "hooks/useEnumSearchParam";
import { useCallback, useMemo, useState } from "react";
import { THEME_GLOBAL_STYLES, color } from "theme/aiInventory";

import { AgentList } from "./AgentList/AgentList.tsx";
import type { AgentStats } from "./AgentList/AgentRow.tsx";
import { HeaderCards } from "./HeaderCards/HeaderCards.tsx";
import type {
  HeaderCardsBreakdowns,
  HeaderCountTrends,
} from "./HeaderCards/HeaderCards.tsx";
import { McpClientsList, McpServersList } from "./McpInventoryPanels.tsx";
import { McpRbacPage } from "./McpRbacPage.tsx";
import { AgentSidecar } from "./Sidecar/AgentSidecar.tsx";
import { ClientSidecar } from "./Sidecar/ClientSidecar.tsx";
import { ConnectorSidecar } from "./Sidecar/ConnectorSidecar.tsx";
import { DrawerShell } from "./Sidecar/DrawerShell.tsx";
import { SecureConnectorModal } from "./Sidecar/SecureConnectorModal.tsx";
import { ServerSidecar } from "./Sidecar/ServerSidecar.tsx";
import { indexServers, matchConnectorToServer } from "./graph/matchServer.ts";
import {
  type AgentStatus,
  type ConnectorWithContext,
  type RiskSeverity,
  agentStatusOf,
  isAgentStatus,
  isRiskSeverity,
} from "./graph/types.ts";
import { useAgentActivities } from "./hooks/useAgentActivity.ts";
import {
  generateServerRisks,
  useAgentGraphData,
} from "./hooks/useAgentGraphData.ts";
import { useAgenticPolicies } from "./hooks/useAgenticPolicies.ts";
import { useEnabledServersForClient } from "./hooks/useEnabledServersForClient.ts";
import { useInventoryData } from "./hooks/useInventoryData.ts";
import { useInventoryHistory } from "./hooks/useInventoryHistory.ts";
import { useManagedMcpServers } from "./hooks/useManagedMcpServers.ts";
import { type McpClient, useMcpClients } from "./hooks/useMcpClients.ts";
import { isConnectorManaged } from "./platforms/enrollment.ts";

export type AiInventoryProps = {
  assessmentId: string;
  jobId: string;
  scopes: ItemAssessmentScope[];
};

type DrawerView =
  | {
      readonly type: "connector";
      readonly connector: AIConnector;
      readonly parentAgent: AIAgent;
    }
  | {
      readonly type: "server";
      readonly server: import("./hooks/useManagedMcpServers.ts").ManagedMcpServer;
      readonly parentAgent?: AIAgent;
    }
  | { readonly type: "agent"; readonly agent: AIAgent }
  | { readonly type: "client"; readonly client: McpClient };

const drawerAgent = (d: DrawerView | undefined): AIAgent | undefined => {
  if (!d) return undefined;
  if (d.type === "agent") return d.agent;
  if (d.type === "connector") return d.parentAgent;
  if (d.type === "server") return d.parentAgent;
  return undefined;
};

export const AiInventory: React.FC<AiInventoryProps> = ({
  assessmentId,
  jobId,
  scopes,
}) => {
  const [statusFilter, setStatusFilter] = useEnumSearchParam(
    "status",
    isAgentStatus
  );
  const [riskFilter, setRiskFilter] = useState<RiskSeverity | undefined>();
  const [drawer, setDrawer] = useState<DrawerView | undefined>();
  const [securingAgent, setSecuringAgent] = useState<
    { agent: AIAgent; connectors: AIConnector[] } | undefined
  >();

  const {
    agents,
    connectors,
    loading: inventoryLoading,
  } = useInventoryData({
    assessmentId,
    jobId,
    scopes,
  });

  const connectorsByAgent = useMemo(() => {
    const map: Record<string, AIConnector[]> = {};
    for (const connector of connectors) {
      const agentId = getConnectorAgentId(connector);
      if (!map[agentId]) map[agentId] = [];
      map[agentId].push(connector);
    }
    return map;
  }, [connectors]);

  const activeAgent = drawerAgent(drawer);

  const activeAgentConnectors = useMemo(
    () =>
      activeAgent ? (connectorsByAgent[getAgentId(activeAgent)] ?? []) : [],
    [activeAgent, connectorsByAgent]
  );

  // Lifted here so ConnectorSidecar can access connector context derived from
  // the parent agent's graph without prop-drilling.
  const { policies: agenticPolicies } = useAgenticPolicies();
  const { servers: managedMcpServers, loading: managedMcpServersLoading } =
    useManagedMcpServers();
  const { clients: mcpClients, loading: mcpClientsLoading } = useMcpClients();
  // For local-agents, the agent IS the registered MCP client. Seed the
  // graph with servers observed in the client's traffic so the gateway +
  // server nodes populate even though no SaaS connector exists. The
  // gateway stamps the registered client id directly onto `azp`, so a
  // strict `where("decodedToken.azp", "==", client.id)` is sufficient.
  const localAgentClient = useMemo(
    () =>
      activeAgent?.platform === "local-agents"
        ? { id: activeAgent.data.id }
        : undefined,
    [activeAgent]
  );
  const {
    observedServerIds: localAgentServerIds,
    loading: localAgentServersLoading,
  } = useEnabledServersForClient(localAgentClient);
  const graphData = useAgentGraphData(
    activeAgent,
    activeAgentConnectors,
    agenticPolicies,
    managedMcpServers,
    localAgentServerIds
  );

  const connectorContextById = useMemo(() => {
    const map = new Map<string, ConnectorWithContext>();
    if (!graphData) return map;
    for (const c of graphData.connectors) {
      map.set(getConnectorId(c.connector), c);
    }
    return map;
  }, [graphData]);

  const connectorContextFor = useCallback(
    (connector: AIConnector): ConnectorWithContext | undefined =>
      connectorContextById.get(getConnectorId(connector)),
    [connectorContextById]
  );

  const platformFilteredAgents = agents;

  const indexedManagedServers = useMemo(
    () => indexServers(managedMcpServers),
    [managedMcpServers]
  );

  const managedServersById = useMemo(
    () => new Map(managedMcpServers.map((s) => [s.id, s])),
    [managedMcpServers]
  );

  // Connector-derived matched servers per agent. Computed up front so the
  // activity probe knows which serverIds to query, and so `perAgentStats` and
  // the activity hook share a single source of truth.
  const matchedServerIdsByAgent = useMemo(() => {
    const map = new Map<string, ReadonlyArray<string>>();
    for (const agent of platformFilteredAgents) {
      const id = getAgentId(agent);
      const matched: string[] = [];
      const seen = new Set<string>();
      for (const c of connectorsByAgent[id] ?? []) {
        if (!isConnectorManaged(c)) continue;
        const match = matchConnectorToServer(c, indexedManagedServers);
        if (match && !seen.has(match.id)) {
          seen.add(match.id);
          matched.push(match.id);
        }
      }
      map.set(id, matched);
    }
    return map;
  }, [platformFilteredAgents, connectorsByAgent, indexedManagedServers]);

  const activitiesByAgent = useAgentActivities(
    platformFilteredAgents,
    matchedServerIdsByAgent
  );

  // Local-agents platform has no SaaS connector records — synthesize one
  // connector per MCP server observed in the client's traffic so it can
  // flow through the same managed/unmanaged summary and "Manage now" flow
  // as a Copilot Studio connector.
  const localAgentConnectorsByAgent = useMemo(() => {
    const map: Record<string, AIConnector[]> = {};
    for (const agent of platformFilteredAgents) {
      if (agent.platform !== "local-agents") continue;
      const agentId = getAgentId(agent);
      const observedServerIds =
        activitiesByAgent.get(agentId)?.connectedServerIds ?? [];
      if (observedServerIds.length === 0) continue;
      map[agentId] = observedServerIds.map((sid) => ({
        platform: "local-agents",
        data: {
          id: sid,
          agentId,
          label: managedServersById.get(sid)?.label ?? sid,
          managed: managedServersById.has(sid),
        },
      }));
    }
    return map;
  }, [platformFilteredAgents, activitiesByAgent, managedServersById]);

  const {
    perAgentStats,
    totalRisks,
    severityTotals,
    ownerTotals,
    statusTotals,
  } = useMemo(() => {
    const perAgentStats: Record<string, AgentStats> = {};
    const severityTotals: Record<RiskSeverity, number> = {
      urgent: 0,
      high: 0,
      medium: 0,
      low: 0,
    };
    const statusTotals: Record<AgentStatus, number> = {
      local: 0,
      managed: 0,
      unmanaged: 0,
    };
    let totalRisks = 0;
    let enrolled = 0;
    let unenrolled = 0;
    for (const agent of platformFilteredAgents) {
      const id = getAgentId(agent);
      const agentConnectors = connectorsByAgent[id] ?? [];
      const severityCounts: Record<RiskSeverity, number> = {
        urgent: 0,
        high: 0,
        medium: 0,
        low: 0,
      };
      const matchedServerIds = matchedServerIdsByAgent.get(id) ?? [];
      const matchedSet = new Set(matchedServerIds);
      const unmanaged = agentConnectors.filter(
        (c) => !isConnectorManaged(c)
      ).length;
      for (const sid of matchedServerIds) {
        const server = managedServersById.get(sid);
        if (!server) continue;
        for (const r of generateServerRisks(server)) {
          severityCounts[r.severity] += 1;
        }
      }
      // Skip `matchedSet` so a SaaS agent with overlapping observed traffic
      // doesn't double-count the connector-derived risks above.
      const observedServerIds =
        activitiesByAgent.get(id)?.connectedServerIds ?? [];
      for (const sid of observedServerIds) {
        if (matchedSet.has(sid)) continue;
        const server = managedServersById.get(sid);
        if (!server) continue;
        for (const r of generateServerRisks(server)) {
          severityCounts[r.severity] += 1;
        }
      }
      // Each unmanaged connector contributes the two governance/audit
      // risks defined in useAgentGraphData (no audit trail + unregistered
      // agent), both high severity.
      severityCounts.high += unmanaged * 2;
      const risks =
        severityCounts.urgent +
        severityCounts.high +
        severityCounts.medium +
        severityCounts.low;
      for (const k of Object.keys(severityCounts) as RiskSeverity[]) {
        severityTotals[k] += severityCounts[k];
      }
      const localConnectors = localAgentConnectorsByAgent[id] ?? [];
      const connectorSummary =
        agent.platform === "local-agents"
          ? {
              unmanaged: localConnectors.filter((c) => !isConnectorManaged(c))
                .length,
              total: localConnectors.length,
            }
          : { unmanaged, total: agentConnectors.length };
      perAgentStats[id] = {
        connectorSummary,
        riskSummary: { high: severityCounts.high, total: risks },
        severityCounts,
      };
      totalRisks += risks;
      if (getAgentOwnerEmail(agent)) enrolled += 1;
      else unenrolled += 1;
      statusTotals[agentStatusOf(agent, unmanaged)] += 1;
    }
    return {
      perAgentStats,
      totalRisks,
      severityTotals,
      ownerTotals: { enrolled, unenrolled },
      statusTotals,
    };
  }, [
    platformFilteredAgents,
    connectorsByAgent,
    matchedServerIdsByAgent,
    activitiesByAgent,
    managedServersById,
    localAgentConnectorsByAgent,
  ]);

  const visibleAgents = useMemo(() => {
    if (!statusFilter && !riskFilter) return platformFilteredAgents;
    return platformFilteredAgents.filter((agent) => {
      if (statusFilter) {
        const stats = perAgentStats[getAgentId(agent)];
        if (
          agentStatusOf(agent, stats?.connectorSummary.unmanaged ?? 0) !==
          statusFilter
        )
          return false;
      }
      if (riskFilter) {
        const s = perAgentStats[getAgentId(agent)]?.severityCounts;
        if (!s || s[riskFilter] === 0) return false;
      }
      return true;
    });
  }, [platformFilteredAgents, statusFilter, riskFilter, perAgentStats]);

  const counts = useMemo(
    () => ({
      status: platformFilteredAgents.length,
      owner: ownerTotals.enrolled,
      agentsConnectors: platformFilteredAgents.length + connectors.length,
      risks: totalRisks,
    }),
    [ownerTotals, platformFilteredAgents, connectors, totalRisks]
  );

  const breakdowns = useMemo<HeaderCardsBreakdowns>(
    () => ({
      status: [
        {
          key: "managed",
          label: "Managed",
          value: statusTotals.managed,
          tone: color.integration,
        },
        {
          key: "unmanaged",
          label: "Unmanaged",
          value: statusTotals.unmanaged,
          tone: color.unmanaged,
        },
        {
          key: "local",
          label: "Local",
          value: statusTotals.local,
          tone: color.agentsConnectors,
        },
      ],
      owner: [
        {
          key: "has-owner",
          label: "Has owner",
          value: ownerTotals.enrolled,
          tone: color.integration,
        },
        {
          key: "no-owner",
          label: "No owner",
          value: ownerTotals.unenrolled,
          tone: color.unmanaged,
        },
      ],
      agentsConnectors: [
        {
          key: "agents",
          label: "Agents",
          value: platformFilteredAgents.length,
          tone: color.agentsConnectors,
        },
        {
          key: "connectors",
          label: "Connectors",
          value: connectors.length,
          tone: color.integration,
        },
      ],
      risks: [
        {
          key: "urgent",
          label: "Urgent",
          value: severityTotals.urgent,
          tone: color.riskUrgent,
        },
        {
          key: "high",
          label: "High",
          value: severityTotals.high,
          tone: color.riskHigh,
        },
        {
          key: "medium",
          label: "Medium",
          value: severityTotals.medium,
          tone: color.riskMedium,
        },
        {
          key: "low",
          label: "Low",
          value: severityTotals.low,
          tone: color.riskLow,
        },
      ],
    }),
    [
      ownerTotals,
      severityTotals,
      statusTotals,
      platformFilteredAgents.length,
      connectors.length,
    ]
  );

  const handleStatusFilter = useCallback(
    (key: string | undefined) => {
      setStatusFilter(isAgentStatus(key) ? key : undefined);
    },
    [setStatusFilter]
  );
  const handleRiskFilter = useCallback((key: string | undefined) => {
    setRiskFilter(isRiskSeverity(key) ? key : undefined);
  }, []);

  const history = useInventoryHistory({ assessmentId });

  const trends = useMemo<HeaderCountTrends>(
    () => ({
      status: history.map((s) => s.status),
      owner: history.map((s) => s.owner),
      agentsConnectors: history.map((s) => s.agentsConnectors),
      risks: history.map((s) => s.risks),
    }),
    [history]
  );

  const handleSecureUnmanaged = useCallback(
    (agent: AIAgent) => {
      const id = getAgentId(agent);
      const agentConnectors =
        agent.platform === "local-agents"
          ? (localAgentConnectorsByAgent[id] ?? [])
          : (connectorsByAgent[id] ?? []);
      const unmanaged = agentConnectors.filter((c) => !isConnectorManaged(c));
      setSecuringAgent({ agent, connectors: unmanaged });
    },
    [connectorsByAgent, localAgentConnectorsByAgent]
  );

  const openAgent = useCallback(
    (agent: AIAgent) => setDrawer({ type: "agent", agent }),
    []
  );
  const openConnector = useCallback(
    (connector: AIConnector) =>
      setDrawer((prev) =>
        prev?.type === "agent"
          ? { type: "connector", connector, parentAgent: prev.agent }
          : prev
      ),
    []
  );
  const openServer = useCallback(
    (server: import("./hooks/useManagedMcpServers.ts").ManagedMcpServer) =>
      setDrawer((prev) =>
        prev?.type === "agent"
          ? { type: "server", server, parentAgent: prev.agent }
          : prev
      ),
    []
  );
  const openInventoryServer = useCallback(
    (server: import("./hooks/useManagedMcpServers.ts").ManagedMcpServer) =>
      setDrawer({ type: "server", server }),
    []
  );
  const openClient = useCallback(
    (client: McpClient) => setDrawer({ type: "client", client }),
    []
  );
  const closeDrawer = useCallback(() => setDrawer(undefined), []);
  const backFromSubview = useCallback(() => {
    setDrawer((prev) => {
      if (!prev) return prev;
      if (prev.type === "connector") {
        return { type: "agent", agent: prev.parentAgent };
      }
      if (prev.type === "server" && prev.parentAgent) {
        return { type: "agent", agent: prev.parentAgent };
      }
      return prev;
    });
  }, []);
  const handleModalClose = useCallback(() => setSecuringAgent(undefined), []);

  const handleSecureFromConnector = useCallback(
    (connector: AIConnector) => {
      const agent = drawerAgent(drawer);
      if (!agent) return;
      setSecuringAgent({ agent, connectors: [connector] });
    },
    [drawer]
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <style>{THEME_GLOBAL_STYLES}</style>
      <HeaderCards
        counts={counts}
        trends={trends}
        breakdowns={breakdowns}
        statusFilter={{
          activeKey: statusFilter,
          onSelect: handleStatusFilter,
        }}
        riskFilter={{ activeKey: riskFilter, onSelect: handleRiskFilter }}
      />
      <Tabs
        items={[
          {
            key: "agents",
            label: "Agents",
            children: (
              <AgentList
                agents={visibleAgents}
                perAgentStats={perAgentStats}
                activitiesByAgent={activitiesByAgent}
                loading={inventoryLoading}
                onSelect={openAgent}
                onSecureUnmanaged={handleSecureUnmanaged}
              />
            ),
          },
          {
            key: "mcp-servers",
            label: "MCP servers",
            children: (
              <McpServersList
                servers={managedMcpServers}
                loading={managedMcpServersLoading}
                onSelect={openInventoryServer}
              />
            ),
          },
          {
            key: "mcp-clients",
            label: "MCP clients",
            children: (
              <McpClientsList
                clients={mcpClients}
                loading={mcpClientsLoading}
                onSelect={openClient}
              />
            ),
          },
          {
            key: "mcp-iam",
            label: "MCP IAM",
            children: <McpRbacPage />,
          },
        ]}
      />
      <DrawerShell open={!!drawer} onClose={closeDrawer}>
        {drawer?.type === "connector" ? (
          <ConnectorSidecar
            connector={drawer.connector}
            context={connectorContextFor(drawer.connector)}
            onClose={closeDrawer}
            onBack={backFromSubview}
            onSecure={handleSecureFromConnector}
          />
        ) : drawer?.type === "server" ? (
          <ServerSidecar
            server={drawer.server}
            onClose={closeDrawer}
            onBack={drawer.parentAgent ? backFromSubview : undefined}
          />
        ) : drawer?.type === "client" ? (
          <ClientSidecar client={drawer.client} onClose={closeDrawer} />
        ) : (
          <AgentSidecar
            agent={drawer?.type === "agent" ? drawer.agent : undefined}
            connectors={activeAgentConnectors}
            graphData={graphData}
            onClose={closeDrawer}
            onConnectorSelect={openConnector}
            onServerSelect={openServer}
            onSecureConnector={handleSecureFromConnector}
            graphLoading={
              activeAgent?.platform === "local-agents" &&
              localAgentServersLoading
            }
          />
        )}
      </DrawerShell>
      <SecureConnectorModal
        open={!!securingAgent}
        agent={securingAgent?.agent}
        connectors={securingAgent?.connectors ?? []}
        onClose={handleModalClose}
      />
    </div>
  );
};
